/**
 * Ad Refresh Manager
 *
 * Manages strategic ad refreshes to maximize revenue while respecting user experience
 * Follows Google AdSense best practices:
 * - Only refresh when ad is in viewport
 * - Pause during user inactivity
 * - Minimum 30-second intervals between refreshes
 * - Respect user preferences
 */

export interface AdRefreshConfig {
  enabled: boolean;
  intervalSeconds: number; // Minimum 30 seconds per AdSense policy
  maxRefreshesPerSession: number;
  pauseOnInactivity: boolean;
  inactivityThresholdSeconds: number;
}

export interface AdRefreshState {
  adSlot: string;
  refreshCount: number;
  lastRefreshTime: number;
  isInViewport: boolean;
  refreshTimer: number | null;
}

export class AdRefreshManager {
  private static config: AdRefreshConfig = {
    enabled: true,
    intervalSeconds: 60, // 60 seconds (conservative, AdSense allows 30+)
    maxRefreshesPerSession: 10,
    pauseOnInactivity: true,
    inactivityThresholdSeconds: 60,
  };

  private static adStates = new Map<string, AdRefreshState>();
  private static observers = new Map<string, IntersectionObserver>();
  private static lastActivityTime = Date.now();
  private static activityListenersAdded = false;

  /**
   * Initialize the refresh manager with custom config
   */
  static initialize(config?: Partial<AdRefreshConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Setup activity tracking
    if (!this.activityListenersAdded && typeof window !== 'undefined') {
      this.setupActivityTracking();
      this.activityListenersAdded = true;
    }

  }

  /**
   * Setup user activity tracking
   */
  private static setupActivityTracking() {
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
    };

    // Track user interactions
    window.addEventListener('mousemove', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('scroll', updateActivity, { passive: true });
    window.addEventListener('click', updateActivity, { passive: true });
    window.addEventListener('touchstart', updateActivity, { passive: true });

  }

  /**
   * Check if user is currently active
   */
  private static isUserActive(): boolean {
    if (!this.config.pauseOnInactivity) return true;

    const timeSinceActivity = (Date.now() - this.lastActivityTime) / 1000;
    return timeSinceActivity < this.config.inactivityThresholdSeconds;
  }

  /**
   * Start refresh tracking for an ad
   */
  static startRefresh(
    adElement: HTMLElement,
    adSlot: string,
    onRefresh: () => void
  ) {
    if (!this.config.enabled) {
      return;
    }

    // Initialize state for this ad
    this.adStates.set(adSlot, {
      adSlot,
      refreshCount: 0,
      lastRefreshTime: Date.now(),
      isInViewport: false,
      refreshTimer: null,
    });

    // Setup viewport tracking
    this.setupViewportTracking(adElement, adSlot, onRefresh);

  }

  /**
   * Setup viewport tracking using Intersection Observer
   */
  private static setupViewportTracking(
    adElement: HTMLElement,
    adSlot: string,
    onRefresh: () => void
  ) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const state = this.adStates.get(adSlot);
          if (!state) return;

          const wasInViewport = state.isInViewport;
          state.isInViewport = entry.isIntersecting;

          if (entry.isIntersecting && !wasInViewport) {
            // Ad entered viewport - start refresh timer
            this.scheduleRefresh(adSlot, onRefresh);
          } else if (!entry.isIntersecting && wasInViewport) {
            // Ad left viewport - pause refresh timer
            this.pauseRefresh(adSlot);
          }
        });
      },
      {
        threshold: 0.5, // Ad must be at least 50% visible
        rootMargin: '0px',
      }
    );

    observer.observe(adElement);
    this.observers.set(adSlot, observer);
  }

  /**
   * Schedule the next ad refresh
   */
  private static scheduleRefresh(adSlot: string, onRefresh: () => void) {
    const state = this.adStates.get(adSlot);
    if (!state) return;

    // Check if we've hit the refresh limit
    if (state.refreshCount >= this.config.maxRefreshesPerSession) {
      return;
    }

    // Clear existing timer
    if (state.refreshTimer) {
      window.clearTimeout(state.refreshTimer);
    }

    // Calculate time until next refresh
    const timeSinceLastRefresh = (Date.now() - state.lastRefreshTime) / 1000;
    const timeUntilRefresh = Math.max(
      0,
      this.config.intervalSeconds - timeSinceLastRefresh
    );

    // Schedule refresh
    state.refreshTimer = window.setTimeout(() => {
      this.executeRefresh(adSlot, onRefresh);
    }, timeUntilRefresh * 1000);

  }

  /**
   * Execute ad refresh
   */
  private static executeRefresh(adSlot: string, onRefresh: () => void) {
    const state = this.adStates.get(adSlot);
    if (!state) return;

    // Don't refresh if user is inactive
    if (!this.isUserActive()) {
      // Reschedule for later
      state.refreshTimer = window.setTimeout(() => {
        this.executeRefresh(adSlot, onRefresh);
      }, this.config.inactivityThresholdSeconds * 1000);
      return;
    }

    // Don't refresh if not in viewport
    if (!state.isInViewport) {
      return;
    }

    // Execute refresh
    try {
      onRefresh();
      state.refreshCount++;
      state.lastRefreshTime = Date.now();


      // Schedule next refresh if under limit
      if (state.refreshCount < this.config.maxRefreshesPerSession) {
        this.scheduleRefresh(adSlot, onRefresh);
      }
    } catch (error) {
    }
  }

  /**
   * Pause refresh for an ad
   */
  private static pauseRefresh(adSlot: string) {
    const state = this.adStates.get(adSlot);
    if (!state || !state.refreshTimer) return;

    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = null;

  }

  /**
   * Stop refresh tracking for an ad (cleanup)
   */
  static stopRefresh(adSlot: string) {
    // Clear timer
    this.pauseRefresh(adSlot);

    // Disconnect observer
    const observer = this.observers.get(adSlot);
    if (observer) {
      observer.disconnect();
      this.observers.delete(adSlot);
    }

    // Remove state
    this.adStates.delete(adSlot);

  }

  /**
   * Get refresh statistics for an ad
   */
  static getRefreshStats(adSlot: string): AdRefreshState | null {
    return this.adStates.get(adSlot) || null;
  }

  /**
   * Update refresh configuration
   */
  static updateConfig(config: Partial<AdRefreshConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable/disable refresh globally
   */
  static setEnabled(enabled: boolean) {
    this.config.enabled = enabled;

    if (!enabled) {
      // Pause all refreshes
      this.adStates.forEach((_, adSlot) => {
        this.pauseRefresh(adSlot);
      });
    }
  }

  /**
   * Cleanup all refresh tracking
   */
  static cleanup() {
    this.adStates.forEach((_, adSlot) => {
      this.stopRefresh(adSlot);
    });

    this.adStates.clear();
    this.observers.clear();

  }

  /**
   * Get overall refresh statistics
   */
  static getOverallStats() {
    const stats = {
      totalAds: this.adStates.size,
      totalRefreshes: 0,
      activeRefreshes: 0,
    };

    this.adStates.forEach((state) => {
      stats.totalRefreshes += state.refreshCount;
      if (state.refreshTimer !== null) {
        stats.activeRefreshes++;
      }
    });

    return stats;
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  AdRefreshManager.initialize();
}
